import {ISearchFilterSet, ISelectedFilters} from '../Types/ISearchFilter';
import {IResultsData, ICachedResults, IResultSummary} from '../Types/IResultsData';
import {getActiveFiltersSelectedItems} from '../Utils/SearchFilterUtils';
import SearchFilterName from '../Enum/SearchFilterName';
import {IBaseSearchParam} from '../Types/ISearchParam';
import {formatDate, isDateExpired} from '../Utils/DateUtil';
import {checkSearchReferenceStatus, getResultFileUrl, loadResultFile} from '../API/SearchApi';
import SearchState from '../Enum/SearchState';
import Environment from '../Enum/Environment';
import {IGenericObject} from '../Types/IGenericObject';
import {IDepotInfo, IDepotSummaryMap} from '../Types/IDepot';
import {
    processDepotSummaryMap,
    processSummary,
    removeDuplicates,
    sortDepotSummaryByDistance
} from '../Utils/ResultsUtils';
import {getSupplierName} from '../Utils/SupplierUtils';
import {getDepotAddress} from '../Utils/PDFUtils';

const DBName: string = 'RPWA_DB';
const DBStores: IGenericObject = {
    results: 'results'
}
const DBVersion: number = 4;
let DB: IDBDatabase;
let DBReq: IDBOpenDBRequest = indexedDB.open(DBName, DBVersion);

// @ts-ignore
const dbErrorHandler = (event: Event) => {
    // handle Error
}

DBReq.onupgradeneeded = () => {
    DB = DBReq.result;

    // Only perform DB reset/deletion if objectStore already exist
    if (DB.objectStoreNames.length > 0) {
        DB.deleteObjectStore(DBStores.results);
    }

    const ObjectStore = DB.createObjectStore(DBStores.results, {
        autoIncrement: false,
        keyPath: ['ref', 'parameters.date', 'parameters.latitude', 'parameters.longitude']
    });

    ObjectStore.createIndex('searchParamIndex', [ 'parameters.date', 'parameters.latitude', 'parameters.longitude'], { unique: false});
    ObjectStore.createIndex('refIndex',  ['ref']);
}

DBReq.onsuccess = () => {
    DB = DBReq.result;
}

DBReq.onerror = dbErrorHandler;

const clearResultsData = () => {
    const transaction = DB.transaction([DBStores.results], 'readwrite');
    const store = transaction.objectStore(DBStores.results);
    store.clear();
}

export const filterResult = (
    filters: ISearchFilterSet,
    data: IResultsData[],
    searchParameters: IBaseSearchParam,
    ignoreDynamicFilter: boolean = false
): IResultsData[] => {
    const selectedFilters: ISelectedFilters | null = getActiveFiltersSelectedItems(filters);
    // if no active filter return original data
    if (
       selectedFilters === null
    ) {
        return data;
    }

    const carTypeFilters: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.CAR_TYPE];
    const carNameFilters: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.CAR_MODEL];
    const carSupplierFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.SUPPLIER];
    const radiusFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.RADIUS];
    const durationFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.DURATION];
    const carClassFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.CAR_CLASS];
    const depotFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.DEPOT];
    const seatsFilter: (string | number | boolean)[] | null = selectedFilters[SearchFilterName.SEATS];

    const depotSupplierPairs: IGenericObject = {};
    if (depotFilter !== null) {
        depotFilter.forEach((item) => {
            // [depot_code, supplier_code]
           const split: string[] = item.toString().split('_');
           depotSupplierPairs[split[0]] = split[1];
        });
    }

    const results: IResultsData[] = [];
    for (let i: number = 0; i < data.length; ++i) {
        const isCarClassMatch = carClassFilter !== null ? carClassFilter.indexOf(data[i].vehicle_data.category) !== -1 : true;
        const isCarTypeMatch = carTypeFilters !== null
            ? carTypeFilters.toString().split(',').indexOf(data[i].vehicle_data.type) !== -1
            : true;
        const isVehicleNameMatch = carNameFilters !== null && ignoreDynamicFilter === false
            ? carNameFilters.indexOf(data[i].vehicle_data.name) !== -1
            : true;
        const isSupplierMatch = carSupplierFilter !== null
            ? carSupplierFilter.indexOf(data[i].supplier_code) !== -1
            : true;
        const isDurationMatch = durationFilter !== null ? durationFilter.indexOf(data[i].duration) !== -1 : true;
        const isSeatsMatch = seatsFilter !== null ? seatsFilter.indexOf(data[i].vehicle_data.seats) !== -1 : true;

        let isInRadius = true;
        let isInDepot = true;

        if (radiusFilter !== null && typeof searchParameters !== 'undefined') {
            isInRadius = data[i].distance <= radiusFilter[0];
        }

        if (depotFilter !== null) {
           isInDepot = depotSupplierPairs.hasOwnProperty(data[i].depot_code) &&
               data[i].supplier_code === depotSupplierPairs[data[i].depot_code]
        }

        if (
            isCarTypeMatch &&
            isVehicleNameMatch &&
            isSupplierMatch &&
            isInRadius &&
            isDurationMatch &&
            isCarClassMatch &&
            isInDepot &&
            isSeatsMatch &&
            typeof data[i].distance !== 'undefined'
        ) {
            results.push(data[i]);
        }
    }
    return results;
}

// https://stackoverflow.com/a/27943
// https://en.wikipedia.org/wiki/Haversine_formula
const getDistanceInKm = (lt1: number, ln1: number, lt2: number, ln2: number) => {
    const earthRadius = 6371;
    const dLat = deg2rad(lt2 - lt1);
    const dLon = deg2rad(ln2 - ln1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lt1)) * Math.cos(deg2rad(lt2)) *
        Math.sin(dLon/2) * Math.sin(dLon/2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return earthRadius * c;
}

const deg2rad = (deg) => {
    return deg * (Math.PI/180)
}

export const decorateResultsWithDistance = (results: IResultsData[], searchParameters: IBaseSearchParam) => {
    return results.map((result: IResultsData) => {
        if (typeof result.distance !== 'undefined') {
           result.distance = getDistanceInKm(result.depot_coordinates.y, result.depot_coordinates.x, searchParameters.latitude, searchParameters.longitude);
        }

        return result;
    })
        .filter((result: IResultsData) => typeof result.distance !== 'undefined');
}

export const sortResultByPrice = (results: IResultsData[]): IResultsData[] => {
    return results.sort((a, b) => {
        return a.per_day_price - b.per_day_price
    });
}

export const saveResultsToStorage = (refId: string, results: IResultsData[], parameters: IBaseSearchParam) => {
    const payload: ICachedResults = {
        timestamp: new Date(),
        ref: refId,
        results,
        parameters
    }

    if (typeof DB === 'undefined') {
        return payload;
    }

    const transaction = DB.transaction([DBStores.results], 'readwrite');
    const store = transaction.objectStore(DBStores.results);
    const storeRequest = store.put(payload);

    storeRequest.onerror = () => {
        // If storage full clear the data
       if (storeRequest.error !== null && storeRequest.error.code === DOMException.QUOTA_EXCEEDED_ERR) {
           clearResultsData();
       }
    };

    return payload;
}

const deleteItemFromStorage = (refId: string): void => {
    const transaction = DB.transaction([DBStores.results], 'readwrite');
    const store = transaction.objectStore(DBStores.results);
    const refIndex = store.index('refIndex');
    const request = refIndex.openCursor(IDBKeyRange.only([refId]));
    request.onsuccess = () => {
        const cursor = request.result;
        if (cursor !== null) {
            cursor.delete();
        }
    }
    request.onerror = dbErrorHandler;
}

export const findResultInStorage = (date: string | Date, lat: number, lng: number): Promise<ICachedResults> => {
    return new Promise<ICachedResults>((resolve, reject) => {
        const searchDate: string = typeof date !== 'string' ? formatDate(date, 'yyyy-MM-dd') : date;
        const transaction = DB.transaction([DBStores.results], 'readonly');
        const store = transaction.objectStore(DBStores.results);
        const request = store.index('searchParamIndex').get([searchDate, lat, lng]);

        request.onsuccess = (event: any) => {
            const result: ICachedResults | undefined = event.target.result;
            const isExpired: boolean = typeof result !== 'undefined' ? isDateExpired(result.timestamp) : true;
            if (typeof result !== 'undefined' && isExpired === true) {
                deleteItemFromStorage(result.ref);
            }

            if (typeof result !== 'undefined' && isExpired === false) {
                resolve(result);
            } else {
                reject(null);
            }

        }

        request.onerror = () => reject(null);
    });
}

const getResultsFromStorage = (refId: string): Promise<ICachedResults> => {
    return new Promise<ICachedResults>((resolve, reject) => {
        const transaction = DB.transaction([DBStores.results], 'readonly');
        const store = transaction.objectStore(DBStores.results);
        const request = store.index('refIndex').get([refId]);

        request.onsuccess = (event: any) => {
            const result: ICachedResults | undefined = event.target.result;
            const isExpired: boolean = typeof result !== 'undefined' ? isDateExpired(result.timestamp) : true;

            // Delete expired record
             if (typeof result !== 'undefined' && isExpired === true) {
                 deleteItemFromStorage(refId);
                 reject({ expired: true, message: 'Record expired' });
             } else if (typeof result !== 'undefined' && isExpired === false) {
                resolve(result);
            } else {
                 reject({
                     expired: false,
                     message: 'Record Not found'
                 })
             }
        }

        request.onerror = () => {
            reject({ expired: false, message: 'DB error' });
        }
    });
}

export const getResults = (env: Environment, refId: string): Promise<ICachedResults> => {
    // Try to check local DB first,
    // return data from storage if exist AND cache is not expired
    return new Promise<ICachedResults>((resolve, reject) => {
        getResultsFromStorage(refId)
            .then((result: ICachedResults) => resolve(result))
            .catch(() => {
                // Ensure if the results are exported
                checkSearchReferenceStatus(env, refId)
                    .then((search) => {
                        if (search.state === SearchState.STATE_CAR_EXPORTED) {
                            getResultFileUrl(env, refId)
                                .then((response) => {
                                    // @Todo handle if file not found.
                                    loadResultFile(response.url)
                                        .then((response) => {
                                            const cached: ICachedResults = saveResultsToStorage(refId, removeDuplicates(response), search.parameters);
                                            resolve(cached);
                                        });
                                });
                        } else {
                            reject({ state: search.state });
                        }
                    });
            });
    });
}

export const getResultSummary = (results: IResultsData[], depotsAtArea: IDepotInfo[]): IResultSummary | null => {
    if (results.length === 0) {
        return null;
    }

    const summary: IResultSummary = {
        models: {},
        types: {},
        depots: {},
        closest: null,
        lowestPerDay: null,
        highestPerDay: null,
        depotSummary: [],
        depotsWithResults: {},
        suppliersWithResults: {}
    }

    let currentMin: number = results[0].per_day_price;
    let currentMax: number = results[0].per_day_price;
    let lowestItem: IResultsData = results[0];
    let closestItem: IResultsData = results[0];
    let highestPerDay: IResultsData = results[0];
    const depots: IDepotSummaryMap = {
        [`${results[0].depot_code}_${results[0].supplier_code}`]: {
            depot_code: results[0].depot_code,
            distance: results[0].distance,
            supplier_code: results[0].supplier_code,
            supplier_name: results[0].supplier_name,
            highestPerDay: results[0],
            lowestPerDay: results[0],
            address: results[0].depot_address,
            depot_coordinates: results[0].depot_coordinates
        }
    };

    results.forEach((item) => {
        if (item.per_day_price < currentMin) {
            currentMin = item.per_day_price;
            lowestItem = item;
        }

        if (item.per_day_price > currentMax) {
            currentMax = item.per_day_price;
            highestPerDay = item;
        }

        if (item.distance < closestItem.distance) {
            closestItem = item;
        }

        if (typeof summary.suppliersWithResults[item.supplier_code] === 'undefined') {
            summary.suppliersWithResults[item.supplier_code] = getSupplierName(item.supplier_code);
        }

        const depotKey: string = `${item.depot_code}_${item.supplier_code}`;
        if (typeof summary.depotsWithResults[depotKey] === 'undefined') {
            summary.depotsWithResults[depotKey] = getDepotAddress(depotsAtArea, depotKey);
        }

        processSummary(summary, item);
        processDepotSummaryMap(depots, item);
    });

    summary.depotSummary = sortDepotSummaryByDistance(depots);
    summary.highestPerDay = highestPerDay;
    summary.lowestPerDay = lowestItem;
    summary.closest = closestItem;

    return summary;
}
